import { UserRole, type User } from '@/types/auth';
import { PLATFORM_FRONTEND_CONFIG } from '@/config/entrepreneurPlatforms';
import { B2C_PLATFORMS, currentPlatform, ePath, resolveSameOriginRedirect, type B2CPlatform } from '@/utils/paths';

/** `details.reason` of the backend's 403 for a session whose account is not active. */
export const ACCOUNT_NOT_ACTIVE_REASON = 'account_not_active';

/** Login `?error=` codes for a staff session ended because its account is not active. */
export const INACTIVE_LOGIN_ERRORS = {
  PENDING: 'account_not_activated',
  DISABLED: 'account_disabled',
} as const;

const OAUTH_REDIRECT_KEY = 'vess.oauth_redirect';

const ONBOARDING_STATES = new Set(['PENDING', 'PENDING_EMAIL_VERIFICATION']);

const ONBOARDING_SURFACES = ['/onboarding', '/consent', '/waiting', '/verify-email'];

/** The page the browser is on: path, query and hash. */
export function currentLocation(): string {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

/** The current URL's `?redirect=`, when it stays on this origin; null otherwise. */
export function requestedRedirect(): string | null {
  const raw = new URLSearchParams(window.location.search).get('redirect');
  return raw ? resolveSameOriginRedirect(raw) : null;
}

/** `path` carrying `key=value`, appended to whatever query string it already has. */
export function withParam(path: string, key: string, value: string): string {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${key}=${encodeURIComponent(value)}`;
}

/** `path` carrying `redirect` as its `?redirect=`; `path` untouched when there is none. */
export function withRedirect(path: string, redirect: string | null): string {
  if (!redirect) return path;
  return withParam(path, 'redirect', redirect);
}

/** `path` carrying where to come back to: the current URL's explicit `?redirect=` when it has one, the page the browser is on otherwise. */
export function carrying(path: string): string {
  return withRedirect(path, requestedRedirect() ?? currentLocation());
}

/** Whether a signed-in account may not use the app yet. A missing `state` is not inactive. */
export function isAccountInactive(
  user: { state?: string | null } | null | undefined,
): user is { state: string } {
  return !!user?.state && user.state !== 'ACTIVE';
}

/** Where a signed-in account must go before it may use anything.
 *
 * Input: the session user and the path being rendered.
 * Output: null for an active account (or one already on its onboarding);
 * `{ kind: 'onboarding', path }` for an entrepreneur still onboarding — Gaia's
 * email verification, or the FoundersHub portal entry whose guard picks the
 * step (consent, onboarding, verification, waiting); `{ kind: 'login', error }`
 * for every other inactive account, whose onboarding is its emailed link. */
export function inactiveAccountRoute(
  user: Pick<User, 'role' | 'state'>,
  pathname: string,
): { kind: 'onboarding'; path: string } | { kind: 'login'; error: string } | null {
  if (!isAccountInactive(user)) return null;
  const state = user.state;
  if (user.role !== UserRole.ENTREPRENEUR || !ONBOARDING_STATES.has(state)) {
    return {
      kind: 'login',
      error: state === 'DISABLED' ? INACTIVE_LOGIN_ERRORS.DISABLED : INACTIVE_LOGIN_ERRORS.PENDING,
    };
  }
  if (ONBOARDING_SURFACES.some((surface) => pathname === ePath(surface))) return null;
  const platform = currentPlatform();
  if (!B2C_PLATFORMS.includes(platform as B2CPlatform)) return null;
  const cfg = PLATFORM_FRONTEND_CONFIG[platform as B2CPlatform];
  const path = cfg.toPath(cfg.onboarding ? cfg.dashboard : (cfg.verifyEmail ?? cfg.dashboard));
  return pathname === path ? null : { kind: 'onboarding', path };
}

/** Remember where an OAuth login should land, across the provider round trip. */
export function rememberOAuthRedirect(): void {
  const redirect = requestedRedirect();
  if (redirect) sessionStorage.setItem(OAUTH_REDIRECT_KEY, redirect);
  else sessionStorage.removeItem(OAUTH_REDIRECT_KEY);
}

/** The redirect an OAuth login started with, consumed once. */
export function takeOAuthRedirect(): string | null {
  const redirect = sessionStorage.getItem(OAUTH_REDIRECT_KEY);
  sessionStorage.removeItem(OAUTH_REDIRECT_KEY);
  return redirect ? resolveSameOriginRedirect(redirect) : null;
}
